import { Location } from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FaqBuilder, InfoDropdown } from 'src/app/components/faq/faq.component';
import { FeatureType } from 'src/app/components/featured-plates/featured-plates.component';
import {
  BYOTermBreakdown,
  BYOTermResponse,
} from 'src/app/models/byoTermResponse';
import { Registration } from 'src/app/models/registration';
import { RegistrationService } from 'src/app/services/registration-service';
import {
  SearchReqResponse,
  SearchService,
} from 'src/app/services/search-service';
import {
  TrackingService,
  TRACKING_SEARCH_TYPE,
} from 'src/app/services/tracking-service';
import GlobalFaqs from "../../global-faqs";

@Component({
  selector: 'pla-byo-search-page',
  templateUrl: './byo-search-page.component.html',
  styleUrls: ['./byo-search-page.component.scss'],
})
export class BYOSearchPageComponent implements OnInit {
  public styleBreakdowns: BYOTermResponse = null;
  public raFeatured = new FeatureType(
    'Recently Added',
    'recently-added'
  );

  @Input() public style: string = 'current';
  @Input('breakdowns') public inputBreakdowns: BYOTermResponse = null; // FROM API ####TGE / ####TGE / TGE####
  @Input('searchDefault') public searchDefault: boolean = true;
  @Input('inlineSearch') public inlineSearch: boolean = false;
  @Input() public gotoResults: boolean = false;
  @Input() public showSwitch: boolean = true;

  public featuredNames: { name: string, plate: string, style: string, link: string }[] = [
    { name: 'JOHN', plate: 'JO11 N??', style: 'current', link: 'JO/11/N--' },
    { name: 'LOREN', plate: 'LO23 N??', style: 'current', link: 'LO/23/N--' },
    { name: 'CHIARA', plate: 'CH14 RA?', style: 'current', link: 'CH/14/RA-' },
    { name: 'LUKE', plate: '???1 UKE?', style: 'current', link: '--/-1/UKE' },
    { name: 'PETER', plate: 'PE73 R??', style: 'current', link: 'PE/73/R--' },
    { name: 'SARAH', plate: 'SA24 H??', style: 'current', link: 'SA/24/H--' },
    { name: 'STEVEN', plate: '?S73 VEN', style: 'current', link: 'ST/73/VEN' },
    { name: 'GEORGE', plate: 'GE02 GE?', style: 'current', link: 'GE/02/GE-' },
    { name: 'PAUL', plate: 'PA11 L??', style: 'current', link: 'PA/11/L--' },
    { name: 'KATIE', plate: 'KA71 E??', style: 'current', link: 'KA/71/E--' },
    { name: 'HUGO', plate: 'HU60 ???', style: 'current', link: 'HU/60/---' },
    { name: 'SIMON', plate: '??51 MON', style: 'current', link: '--/51/MON' },
  ]

  public faq: InfoDropdown[] = GlobalFaqs.GetFaqsByKey('Build Your Own');

  public results: SearchReqResponse;
  public resultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();

  @Output() public resultChangeEvent: EventEmitter<{ loading: boolean, results: SearchReqResponse }> = new EventEmitter<{ loading: boolean, results: SearchReqResponse }>();

  public searchTerm: string = '';
  public loading: boolean = false;
  public hasSearchCriteria: boolean = false;

  public no_character: string = '﹣';
  public any_character: string = '?';
  public wc_character: string = '*';

  constructor(
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private trackingService: TrackingService
  ) { }

  ngOnInit(): void {
    // inline = inside search page
    if (this.inlineSearch) {
      this.styleBreakdowns = this.inputBreakdowns;
      if (this.searchDefault) this.search();
      return;
    }

    this.styleBreakdowns = new BYOTermResponse().construct();
    this.style = this.activatedRoute.snapshot.paramMap.get('style');
    if (this.style == null) {
      // blank slate
      this.style = 'current';
    } else {
      // existing values from route
      var _routeActive = this.styleBreakdowns[this.style] as BYOTermBreakdown;
      _routeActive.construct(
        this.style,
        this.activatedRoute.snapshot.paramMap.get('prefix'),
        this.activatedRoute.snapshot.paramMap.get('numbers'),
        this.activatedRoute.snapshot.paramMap.get('letters')
      );

      this.trackingService.TrackSearch(
        TRACKING_SEARCH_TYPE.BYO,
        `${_routeActive.prefix}${_routeActive.numbers}${_routeActive.letters}`,
        { style: this.style }
      );
    }

    if (!this.searchDefault) return;
    this.search();
  }

  public tableHeaderSearchTerm(): string {
    return this.searchTerm.replace('$', this.wc_character).replace('$', this.wc_character).replace('$', this.wc_character);
  }

  public activeBreakdown(): BYOTermBreakdown {
    if (this.style == null) return new BYOTermBreakdown().construct('current', '', '', '');
    if (this.styleBreakdowns == null) return new BYOTermBreakdown().construct(this.style, '', '', '');
    var _ = this.styleBreakdowns[this.style] as BYOTermBreakdown;
    _.style = this.style; // force style
    return _;
  }

  public doInlineSearch(term: BYOTermBreakdown): void {
    // update model behind UI
    this.style = term.style;
    this.styleBreakdowns[this.style] = term;

    if (this.inlineSearch && !this.gotoResults) {
      this.search();
    } else {
      if (this.gotoResults) {
        this.router.navigate([`/byo-search/${this.style}/${term.prefix}/${term.numbers}/${term.letters}`])
      } else {
        // update route
        this.location.go(
          `/byo-search/${this.style}/${term.prefix}/${term.numbers}/${term.letters}`
        );
        // search without refreshing
        this.search();
      }
    }
  }

  private search(): void {
    if (
      this.activeBreakdown().prefix == '' &&
      this.activeBreakdown().numbers == '' &&
      this.activeBreakdown().letters == ''
    ) {
      this.hasSearchCriteria = false;
      return;
    }

    this.hasSearchCriteria = true;

    this.searchTerm = `${this.activeBreakdown().prefix}${this.activeBreakdown().numbers
      }${this.activeBreakdown().letters}`.replace(/-/gi, '?');

    this.loading = true;
    this.resultChangeEvent.emit({ loading: true, results: null });
    this.searchService.searchBYO(
      this.style,
      this.activeBreakdown().prefix,
      this.activeBreakdown().numbers,
      this.activeBreakdown().letters,
      (_: Registration[]) => {
        // search exact
        this.searchService.searchExact(
          encodeURIComponent(this.searchTerm),
          (exact_results: Registration[]) => {
            this.loading = false;
            this.handleResults([...exact_results, ..._]);
          }
        );
      }
    );
  }

  public handleResults(registrations: Registration[]): void {
    registrations = this.registrationService.formatRegistrations(registrations);
    this.results = new SearchReqResponse('byo_id', registrations);
    this.resultChange.emit(this.results);
    this.resultChangeEvent.emit({ loading: false, results: this.results });
  }
}
